/**
 * ViewBuilder - 5-Step visual view definition editor
 *
 * Steps:
 * 1. Focus Table - Select primary table and basic info
 * 2. Joins - Configure relationship-based joins
 * 3. Visible Fields - Select and order fields with drag-and-drop
 * 4. Filters - Configure default filters
 * 5. Permissions - Set access permissions
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useView, useViewMutations } from "../hooks/useViews";
import { useSchemas } from "../hooks/useSchemas";
import {
  ViewBuilderProvider,
  useViewBuilderContext,
} from "../components/viewBuilder/ViewBuilderContext";
import ViewBuilderStepper, {
  ViewBuilderNavigation,
} from "../components/viewBuilder/ViewBuilderStepper";
import FocusTableStep from "../components/viewBuilder/steps/FocusTableStep";
import JoinsStep from "../components/viewBuilder/steps/JoinsStep";
import FieldsStep from "../components/viewBuilder/steps/FieldsStep";
import FiltersStep from "../components/viewBuilder/steps/FiltersStep";
import PermissionsStep from "../components/viewBuilder/steps/PermissionsStep";
import type {
  ViewCreateFull,
  ViewFieldInlineCreate,
  ViewJoinInlineCreate,
  ViewFilterInlineCreate,
  ViewPermissionInlineCreate,
  ViewFieldUpdateInView,
  ViewJoinUpdateInView,
  ViewPermissionUpdateInView,
  SchemaTableWithColumns,
  SchemaColumnResponse,
} from "../types";

/**
 * Inner component that uses the ViewBuilder context
 */
function ViewBuilderContent() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = !!id && id !== "create";
  const viewId = isEditing ? id : null;

  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  const {
    state,
    loadView,
    setError,
    setSaving,
    clearDraft,
    loadDraft,
    getDraftInfo,
    setFocusTable,
  } = useViewBuilderContext();
  const { view, loading: viewLoading } = useView(workspaceId, viewId);
  const { schemas, loading: schemasLoading } = useSchemas(workspaceId);
  const {
    createView,
    updateView,
    loading: mutationLoading,
  } = useViewMutations(workspaceId);

  const [viewDataLoaded, setViewDataLoaded] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);

  // Load draft from localStorage when creating a new view (not editing)
  useEffect(() => {
    if (!isEditing && schemas.length > 0 && !draftLoaded) {
      // Get draft info before loading to know the focusTableId
      const draftInfo = getDraftInfo();
      const wasLoaded = loadDraft();

      if (wasLoaded && draftInfo?.focusTableId) {
        // Find the table from schemas and set it
        const table = schemas.find((s) => s.id === draftInfo.focusTableId);
        if (table) {
          setFocusTable(table.id, table);
        }
      }
      setDraftLoaded(true);
    }
  }, [isEditing, schemas, draftLoaded, loadDraft, getDraftInfo, setFocusTable]);

  // Load existing view data when editing
  useEffect(() => {
    if (isEditing && view && schemas.length > 0 && !viewDataLoaded) {
      const focusTable = schemas.find(
        (s: SchemaTableWithColumns) => s.id === view.focus_table_id,
      );

      // Helper to find table by ID
      const getTable = (tableId: string) =>
        schemas.find((s) => s.id === tableId);

      // Helper to find column in a table
      const getColumn = (
        tableId: string,
        columnId: string,
      ): SchemaColumnResponse | undefined => {
        const table = getTable(tableId);
        return table?.columns?.find((c) => c.id === columnId);
      };

      // Helper to get table name
      const getTableName = (tableId: string): string => {
        const table = getTable(tableId);
        return table?.display_name || table?.table_name || "Unknown";
      };

      // Build a map of join aliases to their target table IDs for field resolution
      const aliasToTableId: Record<string, string> = {};
      (view.joins || []).forEach((j) => {
        const targetTableId =
          j.schema_relationship?.to_table_id || j.target_table_id;
        if (j.table_alias && targetTableId) {
          aliasToTableId[j.table_alias] = targetTableId;
        }
      });

      // Build a map of join IDs to their mapped IDs for parent_join_id resolution
      const joinIdMap: Record<string, string> = {};

      // Map joins with full metadata resolution
      const mappedJoins = (view.joins || []).map((j, idx) => {
        // Get source/target info from schema_relationship if available
        const rel = j.schema_relationship;
        const sourceTableId =
          rel?.from_table_id || j.source_table_id || view.focus_table_id;
        const targetTableId = rel?.to_table_id || j.target_table_id || "";
        const sourceColumnId = rel?.from_column_id || j.source_column_id || "";
        const targetColumnId = rel?.to_column_id || j.target_column_id || "";

        const sourceTable = getTable(sourceTableId);
        const targetTable = getTable(targetTableId);
        const sourceColumn = getColumn(sourceTableId, sourceColumnId);
        const targetColumn = getColumn(targetTableId, targetColumnId);

        const mappedId = `join-${j.id}`;
        joinIdMap[j.id] = mappedId;

        return {
          id: mappedId,
          relationshipId: j.relationship_id || "",
          relationship: rel || null,
          parentJoinId: j.parent_join_id
            ? joinIdMap[j.parent_join_id] || null
            : null,
          joinType: j.join_type,
          alias: j.table_alias || j.alias || "",
          joinOrder: j.join_order ?? j.sort_order ?? idx,
          sourceTableName:
            sourceTable?.display_name || sourceTable?.table_name || "Unknown",
          sourceColumnName:
            sourceColumn?.display_name ||
            sourceColumn?.column_name ||
            "Unknown",
          targetTableId,
          targetTableName:
            targetTable?.display_name || targetTable?.table_name || "Unknown",
          targetColumnName:
            targetColumn?.display_name ||
            targetColumn?.column_name ||
            "Unknown",
        };
      });

      // Map fields with full column metadata resolution
      const mappedFields = [...(view.fields || [])]
        .sort(
          (a, b) =>
            (a.display_order ?? Number.MAX_SAFE_INTEGER) -
            (b.display_order ?? Number.MAX_SAFE_INTEGER),
        )
        .map((f, idx) => {
          // Determine the table ID for this field
          const tableId = f.source_table_alias
            ? aliasToTableId[f.source_table_alias] || view.focus_table_id
            : view.focus_table_id;

          // Try to get column from the API response first, then fall back to schema lookup
          const column: SchemaColumnResponse = f.column ||
            getColumn(tableId, f.column_id) || {
              id: f.column_id,
              table_id: tableId,
              column_name: f.display_name || "Unknown",
              display_name: f.display_name || "Unknown",
              data_type: "string",
              is_nullable: true,
              is_primary_key: false,
              is_foreign_key: false,
              is_available: true,
            };

          return {
            id: `field-${f.id}`,
            columnId: f.column_id,
            column,
            tableId,
            tableName: getTableName(tableId),
            tableAlias: f.source_table_alias || null,
            displayLabel:
              f.display_label ||
              column.display_name ||
              column.column_name ||
              "Unknown",
            isVisible: f.is_visible ?? true,
            isEditable: f.is_editable ?? false,
            isLinkField: f.is_link_field ?? false,
            isUserIdentifier: f.is_user_identifier ?? false,
            sortOrder: f.display_order ?? idx,
            widthHint: f.width_hint ?? undefined,
            formatType: f.format_type ?? undefined,
          };
        });

      // Map filters if present
      const mappedFilters = view.default_filters
        ? view.default_filters.map((filter) => {
            const field = mappedFields.find(
              (candidate) =>
                candidate.columnId === filter.column_id &&
                (filter.value_json?.source_table_alias == null ||
                  candidate.tableAlias ===
                    filter.value_json.source_table_alias),
            );
            return {
              id: `filter-${filter.id}`,
              columnId: filter.column_id,
              tableAlias:
                filter.value_json?.source_table_alias ||
                field?.tableAlias ||
                null,
              operator: filter.operator,
              value: filter.value_json?.value,
              isLocked: filter.is_locked,
            };
          })
        : (view.filters || []).map((f) => ({
            id: `filter-${f.id}`,
            columnId: f.column_id?.toString() || "",
            tableAlias: f.table_alias || null,
            operator: f.operator,
            value: f.value,
            isLocked: f.is_required || false,
          }));

      // Map existing data to ViewBuilderState format
      loadView({
        viewId: view.id.toString(),
        isEditing: true,
        name: view.name,
        description: view.description || "",
        focusTableId: view.focus_table_id,
        focusTable: focusTable || null,
        selectedFields: mappedFields,
        selectedJoins: mappedJoins,
        filters: mappedFilters,
        permissions: (view.permissions || []).map((p) => ({
          id: `permission-${p.id}`,
          groupId: p.group_id,
          canView: p.can_view,
          canEdit: p.can_edit,
          canDelete: p.can_delete,
          canExport: p.can_export,
          rowScope: p.row_scope ?? "own",
        })),
      });

      setViewDataLoaded(true);
    }
  }, [isEditing, view, schemas, viewDataLoaded, loadView]);

  const handleSave = async () => {
    // Validate
    if (!state.name.trim()) {
      setError("View name is required");
      return;
    }
    if (!state.focusTableId) {
      setError("Please select a focus table");
      return;
    }
    if (state.selectedFields.length === 0) {
      setError("Please select at least one field");
      return;
    }

    setError(null);
    setSaving(true);

    try {
      // Build fields payload
      const fields: ViewFieldInlineCreate[] = state.selectedFields.map(
        (f, idx) => ({
          column_id: f.columnId,
          source_table_alias: f.tableAlias || undefined,
          display_label: f.displayLabel,
          display_order: idx,
          is_visible: f.isVisible,
          is_editable: f.isEditable,
          is_link_field: f.isLinkField,
          is_user_identifier: f.isUserIdentifier,
          width_hint: f.widthHint,
          format_type: f.formatType,
        }),
      );

      // Build joins payload
      const joins: ViewJoinInlineCreate[] = state.selectedJoins.map(
        (j, idx) => ({
          relationship_id: j.relationshipId,
          parent_join_order: j.parentJoinId
            ? state.selectedJoins.findIndex((pj) => pj.id === j.parentJoinId)
            : undefined,
          join_type: j.joinType,
          table_alias: j.alias,
          join_order: idx,
        }),
      );

      // Build filters payload - only include filters with values
      const filters: ViewFilterInlineCreate[] = state.filters
        .filter(
          (f) =>
            f.operator === "is_null" ||
            f.operator === "is_not_null" ||
            (f.value !== null && f.value !== undefined && f.value !== ""),
        )
        .map((f) => {
          // Find the matching field to get the column name
          const matchingField = state.selectedFields.find(
            (field) => field.columnId === f.columnId,
          );
          // Focus fields use their column name; joined fields need their source alias.
          const tableAlias = f.tableAlias || matchingField?.tableAlias;
          const columnName = matchingField?.column.column_name || "unknown";
          return {
            field: tableAlias ? `${tableAlias}.${columnName}` : columnName,
            operator: f.operator,
            value: f.value,
          };
        });

      // Build permissions payload
      const permissions: ViewPermissionInlineCreate[] = state.permissions.map(
        (p) => ({
          group_id: p.groupId,
          can_view: p.canView,
          can_edit: p.canEdit,
          can_delete: p.canDelete,
          can_export: p.canExport,
          row_scope: p.rowScope,
        }),
      );

      if (isEditing && viewId) {
        // Build update payloads with camelCase to match backend ViewDefinitionUpdate schema
        const updateFields: ViewFieldUpdateInView[] = state.selectedFields.map(
          (f, idx) => ({
            columnId: f.columnId,
            sourceTableAlias: f.tableAlias || undefined,
            displayLabel: f.displayLabel,
            displayOrder: idx,
            isVisible: f.isVisible,
            isEditable: f.isEditable,
            isLinkField: f.isLinkField,
            isUserIdentifier: f.isUserIdentifier,
            widthHint: f.widthHint,
            formatType: f.formatType,
          }),
        );

        const updateJoins: ViewJoinUpdateInView[] = state.selectedJoins.map(
          (j, idx) => ({
            relationshipId: j.relationshipId,
            parentJoinId: j.parentJoinId || undefined,
            joinType: j.joinType,
            tableAlias: j.alias,
            joinOrder: idx,
          }),
        );

        const updatePermissions: ViewPermissionUpdateInView[] =
          state.permissions.map((p) => ({
            groupId: p.groupId,
            canView: p.canView,
            canEdit: p.canEdit,
            canDelete: p.canDelete,
            canExport: p.canExport,
            rowScope: p.rowScope,
          }));

        // Update existing view with full payload
        await updateView(viewId, {
          name: state.name,
          description: state.description || undefined,
          isTemplate: state.isTemplate,
          isPublic: state.isPublic,
          fields: updateFields,
          joins: updateJoins.length > 0 ? updateJoins : [],
          permissions: updatePermissions.length > 0 ? updatePermissions : [],
          default_filters: filters,
        });
      } else {
        // Create new view with full payload
        const data: ViewCreateFull = {
          name: state.name,
          description: state.description || undefined,
          focus_table_id: state.focusTableId!,
          is_template: state.isTemplate || undefined,
          is_public: state.isPublic || undefined,
          fields,
          joins: joins.length > 0 ? joins : undefined,
          default_filters: filters.length > 0 ? filters : undefined,
          permissions: permissions.length > 0 ? permissions : undefined,
        };
        await createView(data);
      }

      // Clear draft on successful save
      clearDraft();
      navigate(`/workspace/${workspaceId}/myvault/views`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save view");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/workspace/${workspaceId}/myvault/views`);
  };

  // Render current step content
  const renderStepContent = () => {
    switch (state.currentStep) {
      case 0:
        return <FocusTableStep workspaceId={workspaceId} />;
      case 1:
        return <JoinsStep workspaceId={workspaceId} />;
      case 2:
        return <FieldsStep workspaceId={workspaceId} />;
      case 3:
        return <FiltersStep workspaceId={workspaceId} />;
      case 4:
        return <PermissionsStep workspaceId={workspaceId} />;
      default:
        return null;
    }
  };

  // Loading state
  const loading =
    schemasLoading || (isEditing && (viewLoading || !viewDataLoaded));

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton variant="plain" onClick={handleCancel}>
          <ArrowBackIcon />
        </IconButton>
        <Typography level="h2">
          {isEditing ? "Edit View" : "Create View"}
        </Typography>
      </Box>

      {/* Stepper */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <ViewBuilderStepper
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={state.isSaving}
          />
        </CardContent>
      </Card>

      {/* Step Content */}
      <Box sx={{ mb: 3 }}>{renderStepContent()}</Box>

      {/* Navigation */}
      <Card variant="outlined">
        <CardContent>
          <ViewBuilderNavigation
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={state.isSaving || mutationLoading}
          />
        </CardContent>
      </Card>
    </Box>
  );
}

/**
 * ViewBuilder - Main component wrapped with context provider
 */
export default function ViewBuilder() {
  return (
    <ViewBuilderProvider>
      <ViewBuilderContent />
    </ViewBuilderProvider>
  );
}
